/**
 * ระบบเสียง (Sound System) สำหรับ Wickhaven
 *
 * สร้างเอฟเฟกต์เสียงสังเคราะห์คุณภาพสูงแบบ Procedural Audio ด้วย Web Audio API
 * - โหลดไว ทำงานได้ทันที 100% ไม่ต้องรอโหลดไฟล์เสียงภายนอกขนาดใหญ่
 * - ความหน่วงต่ำ (Zero latency)
 * - รองรับ Mobile, Desktop, PWA, Capacitor
 * - ปรับระดับเสียงและเปิด/ปิดเสียงได้ตามต้องการ
 */

class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private enabled = true;
  private volume = 0.8;
  private initialized = false;

  constructor() {
    // โหลดการตั้งค่าจาก localStorage
    try {
      const saved = localStorage.getItem("wickhaven.sound");
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.enabled === "boolean") this.enabled = parsed.enabled;
        if (typeof parsed.volume === "number") this.volume = Math.max(0, Math.min(1, parsed.volume));
      }
    } catch {
      // ใช้ค่าเริ่มต้น
    }
  }

  /** เริ่มต้น AudioContext เมื่อมีการคลิกหรือสัมผัสหน้าจอครั้งแรก */
  public init(): void {
    if (this.initialized) return;
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
      this.masterGain = this.ctx.createGain();
      this.sfxGain = this.ctx.createGain();

      this.masterGain.gain.setValueAtTime(this.enabled ? 1 : 0, this.ctx.currentTime);
      this.sfxGain.gain.setValueAtTime(this.volume, this.ctx.currentTime);

      this.sfxGain.connect(this.masterGain);
      this.masterGain.connect(this.ctx.destination);
      this.initialized = true;

      // Unlock browser audio context
      if (this.ctx.state === "suspended") {
        const unlock = () => {
          this.ctx?.resume().then(() => {
            document.removeEventListener("pointerdown", unlock);
            document.removeEventListener("keydown", unlock);
          }).catch(() => {});
        };
        document.addEventListener("pointerdown", unlock, { once: true });
        document.addEventListener("keydown", unlock, { once: true });
      }
    } catch {
      // Audio ไม่พร้อมใช้งาน ไม่บล็อกเกม
    }
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(enabled ? 1 : 0, this.ctx.currentTime, 0.05);
    }
    this.saveSettings();
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.sfxGain && this.ctx) {
      this.sfxGain.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
    }
    this.saveSettings();
  }

  public getVolume(): number {
    return this.volume;
  }

  private saveSettings(): void {
    try {
      localStorage.setItem("wickhaven.sound", JSON.stringify({ enabled: this.enabled, volume: this.volume }));
    } catch {}
  }

  private getDestination(): AudioNode | null {
    if (!this.initialized) this.init();
    if (!this.enabled || !this.ctx || !this.sfxGain) return null;
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
    return this.sfxGain;
  }

  /** เสียงดาบ/การโจมตีกายภาพโดนเป้าหมาย */
  public playHit(type: "melee" | "crit" | "hurt" | "slash" | "punch" | "stab" = "melee"): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;

    const t = ctx.currentTime;
    if (type === "crit") {
      // คริติคอล: เสียงกระแทกหนัก + เบสดังสะเทือน + เสียงคมดาบสองจังหวะ
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(140, t);
      osc.frequency.exponentialRampToValueAtTime(35, t + 0.25);

      gain.gain.setValueAtTime(0.8, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(t);
      osc.stop(t + 0.3);

      // เสียงกระทบโลหะ/ประกายไฟ
      this.playNoise(0.12, 1200, 300);
    } else if (type === "punch") {
      // หมัดชก: Thud แน่น ๆ
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(160, t);
      osc.frequency.exponentialRampToValueAtTime(45, t + 0.15);

      gain.gain.setValueAtTime(0.7, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(t);
      osc.stop(t + 0.16);
      this.playNoise(0.06, 600, 150);
    } else if (type === "stab") {
      // มีดสั้นแทงเฉือน: เสียงหวีดแหลมเร็ว
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(650, t);
      osc.frequency.exponentialRampToValueAtTime(180, t + 0.1);

      gain.gain.setValueAtTime(0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(t);
      osc.stop(t + 0.1);
      this.playNoise(0.08, 2400, 800);
    } else if (type === "hurt") {
      // ผู้เล่นโดนตี: เสียงทึบต่ำ
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(110, t);
      osc.frequency.exponentialRampToValueAtTime(30, t + 0.2);

      gain.gain.setValueAtTime(0.6, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(t);
      osc.stop(t + 0.2);
    } else {
      // โจมตีกายภาพปกติ (ดาบฟัน)
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(280, t);
      osc.frequency.exponentialRampToValueAtTime(70, t + 0.14);

      gain.gain.setValueAtTime(0.5, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(t);
      osc.stop(t + 0.15);
      this.playNoise(0.07, 1800, 400);
    }
  }

  /** เสียงยิงธนู / ปล่อยลูกศร */
  public playBowShoot(): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    // สายธนูดีด (String twang)
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(420, t);
    osc.frequency.exponentialRampToValueAtTime(140, t + 0.12);

    gain.gain.setValueAtTime(0.6, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.14);

    // เสียงลูกศรแหวกลม
    this.playNoise(0.12, 3200, 1200);
  }

  /** เสียงพลาด (Miss / Dodge) */
  public playMiss(): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    // เสียงลมวืดเบา ๆ
    this.playNoise(0.15, 800, 200, 0.25);
  }

  /** เสียงบล็อก (Shield Block) */
  public playBlock(): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    // เสียงโล่เหล็กปะทะคมดาบกริ๊งสะท้อน
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(720, t);
    osc.frequency.exponentialRampToValueAtTime(440, t + 0.2);

    gain.gain.setValueAtTime(0.7, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.22);
    this.playNoise(0.05, 3500, 1000, 0.4);
  }

  /** เสียงสกิลแยกตามประเภทหรือ glyph */
  public playSkill(glyph: string): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    switch (glyph) {
      case "flame":
      case "inferno": {
        // เปลวไฟลุกโหม (Fire whoosh & burst)
        this.playNoise(0.35, 1200, 200, 0.6);
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(180, t);
        osc.frequency.linearRampToValueAtTime(90, t + 0.3);
        gain.gain.setValueAtTime(0.5, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.35);
        break;
      }
      case "frost":
      case "blizzard": {
        // น้ำแข็งเย็นยะเยือก (Ice shatter shimmer)
        const notes = [880, 1174, 1567];
        notes.forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, t + idx * 0.04);
          gain.gain.setValueAtTime(0.3, t + idx * 0.04);
          gain.gain.exponentialRampToValueAtTime(0.001, t + idx * 0.04 + 0.28);
          osc.connect(gain);
          gain.connect(dest);
          osc.start(t + idx * 0.04);
          osc.stop(t + idx * 0.04 + 0.3);
        });
        this.playNoise(0.2, 4000, 1500, 0.3);
        break;
      }
      case "spark":
      case "lightning": {
        // สายฟ้าฟาด (Thunder clap & high zap)
        this.playNoise(0.18, 5000, 800, 0.7);
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(980, t);
        osc.frequency.exponentialRampToValueAtTime(110, t + 0.22);
        gain.gain.setValueAtTime(0.6, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.24);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.24);
        break;
      }
      case "meteor": {
        // อุกกาบาตตก (Low whistle descending then massive explosion)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(600, t);
        osc.frequency.exponentialRampToValueAtTime(65, t + 0.3);
        gain.gain.setValueAtTime(0.4, t);
        gain.gain.linearRampToValueAtTime(0.8, t + 0.25);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.55);
        this.playNoise(0.45, 800, 60, 0.8);
        break;
      }
      case "mend":
      case "heal":
      case "sanctuary":
      case "bless": {
        // เสียงฮีล/เวทมนตร์ศักดิ์สิทธิ์ (Celestial harmonic chime)
        const chord = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
        chord.forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, t + idx * 0.05);
          gain.gain.setValueAtTime(0.28, t + idx * 0.05);
          gain.gain.exponentialRampToValueAtTime(0.001, t + idx * 0.05 + 0.45);
          osc.connect(gain);
          gain.connect(dest);
          osc.start(t + idx * 0.05);
          osc.stop(t + idx * 0.05 + 0.5);
        });
        break;
      }
      case "roar":
      case "roar2": {
        // คำรามยั่วยุ (Deep vibrating roar resonance)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(95, t);
        osc.frequency.linearRampToValueAtTime(140, t + 0.15);
        osc.frequency.exponentialRampToValueAtTime(50, t + 0.4);
        gain.gain.setValueAtTime(0.7, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.45);
        this.playNoise(0.35, 700, 150, 0.4);
        break;
      }
      case "bash":
      case "slam":
      case "quake": {
        // กระแทกพื้นสะเทือน (Heavy seismic impact)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(220, t);
        osc.frequency.exponentialRampToValueAtTime(40, t + 0.3);
        gain.gain.setValueAtTime(0.8, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.32);
        this.playNoise(0.2, 900, 100, 0.6);
        break;
      }
      case "teleport": {
        // วาร์ป / เทเลพอร์ต (Cosmic frequency glide)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(240, t);
        osc.frequency.exponentialRampToValueAtTime(1400, t + 0.28);
        gain.gain.setValueAtTime(0.6, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.32);
        break;
      }
      default: {
        // บัฟหรือสกิลทั่วไป (Power aura shimmer)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(330, t);
        osc.frequency.exponentialRampToValueAtTime(660, t + 0.22);
        gain.gain.setValueAtTime(0.4, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.connect(gain);
        gain.connect(dest);
        osc.start(t);
        osc.stop(t + 0.25);
        break;
      }
    }
  }

  /** เสียงเลเวลอัป (Triumphant fanfare jingle) */
  public playLevelUp(): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    // โน้ตเพลงแห่งชัยชนะ: C4, E4, G4, C5, E5
    const fanfare = [
      { f: 261.63, d: 0.12 },
      { f: 329.63, d: 0.12 },
      { f: 392.00, d: 0.12 },
      { f: 523.25, d: 0.20 },
      { f: 659.25, d: 0.38 },
    ];

    let start = t;
    fanfare.forEach((n) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(n.f, start);

      gain.gain.setValueAtTime(0.45, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + n.d);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(start);
      osc.stop(start + n.d);
      start += n.d * 0.85;
    });
  }

  /** เสียงเก็บไอเทม / โกลด์ */
  public playLoot(): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    // เสียงเหรียญกริ๊งสองจังหวะ
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(987.77, t); // B5
    gain1.gain.setValueAtTime(0.35, t);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc1.connect(gain1);
    gain1.connect(dest);
    osc1.start(t);
    osc1.stop(t + 0.1);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(1318.51, t + 0.07); // E6
    gain2.gain.setValueAtTime(0.35, t + 0.07);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc2.connect(gain2);
    gain2.connect(dest);
    osc2.start(t + 0.07);
    osc2.stop(t + 0.22);
  }

  /** เสียงกดปุ่ม UI */
  public playClick(): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.04);
    gain.gain.setValueAtTime(0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.05);
  }

  /** สร้างสัญญาณ White Noise สำหรับเสียงระเบิด, ลม, หรือการปะทะ */
  private playNoise(duration: number, startFreq: number, endFreq: number, vol = 0.5): void {
    const dest = this.getDestination();
    const ctx = this.ctx;
    if (!dest || !ctx) return;

    try {
      const bufferSize = ctx.sampleRate * duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const noise = ctx.createBufferSource();
      noise.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      const t = ctx.currentTime;
      filter.frequency.setValueAtTime(startFreq, t);
      filter.frequency.exponentialRampToValueAtTime(Math.max(20, endFreq), t + duration);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(vol, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(dest);

      noise.start(t);
      noise.stop(t + duration);
    } catch {}
  }
}

export const sound = new SoundManager();
